import { A } from '@solidjs/router';
import { createEffect, createSignal, onCleanup, Show } from 'solid-js';
import PublicLayout from '../PublicLayout';

export default function GhostInventoryPostVi() {
  const [heroFailed, setHeroFailed] = createSignal(false);
  const [alertUiFailed, setAlertUiFailed] = createSignal(false);
  const [cycleCountFailed, setCycleCountFailed] = createSignal(false);

  createEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline:
        'Bóng Ma Trong Hệ Thống: Xóa Bỏ Hàng Tồn Ảo Và Hàng Tồn Kho Chết',
      description:
        'Hàng tồn ảo và hàng tồn kho chết là những kẻ giết lợi nhuận thầm lặng. Tìm hiểu cách kiểm kê vòng, điểm đặt hàng tối ưu, theo dõi lô FIFO và đồng bộ thời gian thực giúp loại bỏ chúng.',
      datePublished: '2026-05-06',
      dateModified: '2026-05-06',
      image: 'https://khoai.io/blog-ghost-inventory.jpg',
      url: 'https://khoai.io/vi/blogs/ghost-inventory',
      inLanguage: 'vi',
      author: {
        '@type': 'Organization',
        name: 'khoai.io',
        url: 'https://khoai.io',
      },
      publisher: {
        '@type': 'Organization',
        name: 'khoai.io',
        url: 'https://khoai.io',
        logo: {
          '@type': 'ImageObject',
          url: 'https://khoai.io/og-welcome.jpg',
        },
      },
      isPartOf: {
        '@type': 'Blog',
        name: 'Khoai Blog',
        url: 'https://khoai.io/blogs',
      },
    };

    let script = document.getElementById(
      'ld-json-blog-ghost-inventory-vi'
    ) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'ld-json-blog-ghost-inventory-vi';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);

    let bcScript = document.getElementById(
      'ld-json-blog-ghost-inventory-vi-bc'
    ) as HTMLScriptElement | null;
    if (!bcScript) {
      bcScript = document.createElement('script');
      bcScript.type = 'application/ld+json';
      bcScript.id = 'ld-json-blog-ghost-inventory-vi-bc';
      document.head.appendChild(bcScript);
    }
    bcScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Trang chủ',
          item: 'https://khoai.io',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Blog',
          item: 'https://khoai.io/vi/blogs',
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: schema.headline,
          item: schema.url,
        },
      ],
    });
  });

  onCleanup(() => {
    document.getElementById('ld-json-blog-ghost-inventory-vi')?.remove();
    document.getElementById('ld-json-blog-ghost-inventory-vi-bc')?.remove();
  });

  const h2 = 'mt-12 text-2xl font-extrabold text-gray-900 sm:text-3xl';
  const h3 = 'mt-8 text-xl font-bold text-gray-900';
  const p = 'mt-4 text-base leading-8 text-gray-600';
  const ul = 'mt-4 list-disc space-y-2 pl-6 text-base leading-7 text-gray-600';
  const imgFallback =
    'flex min-h-[320px] w-full items-center justify-center rounded-2xl bg-gray-100 text-sm text-gray-400';

  return (
    <PublicLayout>
      <article class="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav class="mb-8 flex items-center gap-2 text-sm text-gray-400">
          <A href="/vi/blogs" class="hover:text-amber-600">
            Blog
          </A>
          <span>/</span>
          <span class="text-gray-600">Kiểm soát tồn kho</span>
        </nav>

        {/* Header */}
        <header>
          <span class="rounded-full bg-amber-100 px-3 py-0.5 text-xs font-semibold text-amber-700">
            Kiểm soát tồn kho
          </span>
          <h1 class="mt-4 text-3xl font-extrabold leading-tight text-gray-900 sm:text-4xl lg:text-5xl">
            Bóng Ma Trong Hệ Thống: Xóa Bỏ Hàng Tồn Ảo Và Hàng Tồn Kho Chết
          </h1>
          <p class="mt-4 text-lg leading-relaxed text-gray-500">
            Hàng tồn ảo và hàng tồn kho chết là những kẻ giết lợi nhuận thầm
            lặng. Tìm hiểu cách kiểm kê vòng, điểm đặt hàng tối ưu, theo dõi lô
            FIFO và đồng bộ thời gian thực giúp loại bỏ chúng vĩnh viễn.
          </p>
          <div class="mt-5 flex items-center gap-4 text-sm text-gray-400">
            <span>6 tháng 5, 2026</span>
            <span>·</span>
            <span>9 phút đọc</span>
          </div>
        </header>

        {/* Hero image — 1200×628 */}
        <div class="mt-10 overflow-hidden rounded-2xl">
          <Show
            when={!heroFailed()}
            fallback={
              <div class={imgFallback} style={{ 'min-height': '340px' }}>
                Khách hàng thất vọng nhìn vào thông báo Hết Hàng, kho hàng lộn
                xộn ở phía sau mờ dần
              </div>
            }
          >
            <img
              src="/blog-ghost-inventory.jpg"
              alt="Khách hàng thất vọng nhìn vào thông báo Hết Hàng trên điện thoại, phía sau là kho hàng mờ nhạt"
              width={1200}
              height={628}
              class="w-full object-cover"
              onError={() => setHeroFailed(true)}
            />
          </Show>
        </div>

        {/* ── Nội dung ── */}
        <div class="mt-12">
          {/* Đoạn mở đầu */}
          <p class={p}>
            Email đến lúc 9:14 sáng thứ Ba. Dòng tiêu đề:{' '}
            <em>"Cập nhật về đơn hàng #8841 của bạn."</em> Nội dung, ba câu:{' '}
            <em>
              "Xin lỗi, một trong những sản phẩm trong đơn hàng của bạn hiện
              không còn hàng. Chúng tôi đã hủy đơn hàng và hoàn tiền đầy đủ.
              Chúng tôi xin lỗi vì sự bất tiện này."
            </em>
          </p>
          <p class={p}>
            Khách hàng đặt đơn đó hai ngày trước. Trang sản phẩm ghi "Còn hàng."
            Họ đã thông báo với nhóm của mình rằng hàng đang trên đường về. Bây
            giờ họ đang truy cập trang web của đối thủ cạnh tranh của bạn.
          </p>
          <p class={p}>
            Đây là khoảnh khắc{' '}
            <strong class="font-semibold text-gray-900">hàng tồn ảo</strong>{' '}
            chuyển từ sự cố vận hành thành vấn đề kinh doanh thực sự. Hệ thống
            tồn kho báo bạn còn hàng. Thực tế không có. Và hệ quả từ một sai
            lệch đó — đơn hàng bị mất, chi phí xử lý hoàn tiền, khách hàng có
            thể không bao giờ quay lại — âm thầm diễn ra trên hàng chục SKU, mỗi
            tuần, trong những kho hàng vẫn đang tin vào số liệu mà chính họ
            không hoàn toàn tin tưởng.
          </p>

          {/* Mục 1 */}
          <h2 class={h2}>Chi Phí Thực Sự Của Việc Hết Hàng</h2>
          <p class={p}>
            Các ước tính trong ngành cho thấy việc bỏ giỏ hàng do hết hàng trong
            thương mại điện tử tương đương 3–4 lần giá trị đơn hàng bị mất. Bạn
            không chỉ mất giao dịch hôm nay — bạn đang mất{' '}
            <strong class="font-semibold text-gray-900">
              giá trị vòng đời khách hàng
            </strong>
            . Một người mua B2B bị hủy đơn một lần sẽ thử nhà cung cấp khác cho
            đơn hàng tiếp theo. Nếu nhà cung cấp đó giao hàng đúng hẹn, bạn có
            thể không bao giờ gặp lại họ nữa.
          </p>
          <p class={p}>
            Thiệt hại cộng dồn không dừng ở đó. Các sàn thương mại điện tử như
            Shopee, Lazada và Tiki phạt người bán vì tỷ lệ hủy đơn cao. Tỷ lệ
            hủy cao làm giảm xếp hạng trong kết quả tìm kiếm, kéo theo lượng
            người tìm thấy gian hàng giảm, rồi đến doanh thu giảm. Một vấn đề
            hàng tồn ảo bắt đầu trong kho hàng và kết thúc trong kênh marketing
            của bạn.
          </p>
          <p class={p}>
            Ở mặt kia của cùng một đồng xu là{' '}
            <strong class="font-semibold text-gray-900">
              hàng tồn kho chết
            </strong>{' '}
            — hàng hóa đã nhập về, không bán được, và đang chiếm diện tích kệ và
            kho mà bạn đang trả tiền thuê. Nếu một SKU không có giao dịch trong
            180 ngày, đó không còn là tồn kho nữa. Đó là một khoản vay cho chính
            mình với lãi suất bằng không, được bảo đảm bằng những sản phẩm cuối
            cùng có thể phải thanh lý lỗ.
          </p>

          {/* Mục 2 */}
          <h2 class={h2}>Hàng Tồn Ảo Hình Thành Như Thế Nào</h2>
          <p class={p}>
            Hàng tồn ảo không xuất hiện từ không khí. Nó tích lũy qua những điểm
            thất bại có thể dự đoán được — hầu hết vô hình cho đến khi một đơn
            hàng của khách hàng phơi bày chúng:
          </p>
          <ul class={ul}>
            <li>
              <strong class="font-semibold text-gray-900">
                Bán đa kênh mà không đồng bộ thời gian thực.
              </strong>{' '}
              Một đơn vị bán trên gian hàng Shopee của bạn. Hệ thống tồn kho
              chưa biết — hoặc biết sau 30 phút qua cập nhật theo lô. Trong
              khoảng thời gian đó, cùng một đơn vị được phân bổ cho một đơn hàng
              trực tiếp khác. Bạn đang có một sản phẩm và hai khách hàng đã xác
              nhận.
            </li>
            <li>
              <strong class="font-semibold text-gray-900">
                Hàng trả về chưa được nhập lại kho.
              </strong>{' '}
              Hàng trả về đã đến, nó đang ở trong kho, nhưng chưa được kiểm tra,
              nhập lại kệ hoặc ghi nhận. Hệ thống vẫn báo đã bán. Đơn vị đó nằm
              trong thùng hàng trả lại hai tuần mà không ai biết nó đang có sẵn.
            </li>
            <li>
              <strong class="font-semibold text-gray-900">
                Hàng hỏng chưa được xuất kho khỏi hệ thống.
              </strong>{' '}
              Mười hai đơn vị bị ướt khi giao hàng. Sáu cái đã bỏ đi. Ai đó quên
              ghi điều chỉnh. Hệ thống vẫn hiển thị mười hai đơn vị có thể bán.
            </li>
            <li>
              <strong class="font-semibold text-gray-900">
                Sai số kiểm kê thủ công tích lũy theo thời gian.
              </strong>{' '}
              Mỗi sai số nhỏ đều có thể chịu được. Sáu tháng sai số nhỏ tạo ra
              một bảng kiểm kê sai về mặt cơ cấu — lệch 5–15% trên những SKU có
              tốc độ luân chuyển cao nhất.
            </li>
          </ul>
          <p class={p}>
            Không có nguyên nhân nào trong số này đòi hỏi sự bất cẩn. Chúng chỉ
            cần một hệ thống không thể ghi lại thực tế đủ nhanh để duy trì đồng
            bộ với nó.
          </p>

          {/* Mục 3 */}
          <h2 class={h2}>Cái Bẫy Của Tồn Kho An Toàn</h2>
          <p class={p}>
            Phản ứng thông thường trước nỗi lo về hàng tồn ảo là đặt hàng nhiều
            hơn. Nếu bạn không tin vào con số của mình, bạn giữ thêm bộ đệm.
            Điều này giải quyết vấn đề hết hàng bằng cách tạo ra vấn đề hàng tồn
            kho chết. Cả hai đều tiêu diệt lợi nhuận. Chúng chỉ làm vậy qua các
            cơ chế khác nhau.
          </p>
          <p class={p}>
            Cách tiếp cận tinh gọn hơn là tính toán{' '}
            <strong class="font-semibold text-gray-900">
              điểm đặt hàng lại
            </strong>{' '}
            dựa trên dữ liệu thực tế thay vì nỗi sợ. Công thức rất đơn giản:
          </p>
          <div class="mt-6 rounded-2xl border border-gray-200 bg-gray-50 px-7 py-6 font-mono text-sm text-gray-700">
            <p>
              <span class="font-semibold text-gray-900">Điểm Đặt Hàng Lại</span>{' '}
              = (Mức Sử Dụng Trung Bình Mỗi Ngày × Thời Gian Giao Hàng) + Tồn
              Kho An Toàn
            </p>
            <p class="mt-3 text-xs text-gray-400">
              Tồn Kho An Toàn = Z × σ(cầu) × √(Thời Gian Giao Hàng)
              <br />
              Với phần lớn doanh nghiệp vừa và nhỏ: Tồn Kho An Toàn ≈ 1,65 × độ
              lệch chuẩn cầu mỗi ngày × √(thời gian giao hàng theo ngày) — mức
              dịch vụ 95% mà không cần bằng thống kê.
            </p>
          </div>
          <p class={p}>
            Từ quan trọng trong công thức đó là <em>thực tế</em>. Mức sử dụng
            trung bình mỗi ngày được tính từ lịch sử giao dịch thực, không phải
            cảm tính. Thời gian giao hàng đo từ đơn đặt hàng, không phải lời hứa
            của nhà cung cấp. Khi điểm đặt hàng lại được xây dựng trên dữ liệu
            sạch, bạn đặt hàng đúng những gì toán học yêu cầu — không tích trữ
            bộ đệm thừa, không tái đặt hàng khẩn cấp với giá gấp đôi.
          </p>

          {/* Stock alert UI image — 800×500 */}
          <div class="mt-10 overflow-hidden rounded-2xl">
            <Show
              when={!alertUiFailed()}
              fallback={
                <div class={imgFallback} style={{ 'min-height': '280px' }}>
                  Giao diện thông báo cảnh báo hàng sắp hết trên di động
                </div>
              }
            >
              <img
                src="/blog-stock-alert-ui.jpg"
                alt="Giao diện thông báo di động sạch sẽ hiển thị cảnh báo hàng sắp hết: Tên sản phẩm — còn 2 đơn vị, kèm nút Đặt Hàng Ngay"
                width={800}
                height={500}
                class="w-full object-cover"
                loading="lazy"
                onError={() => setAlertUiFailed(true)}
              />
            </Show>
          </div>

          {/* Khoai callout */}
          <div class="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-6">
            <p class="text-base leading-8 text-gray-700">
              <strong class="font-semibold text-gray-900">
                Khoai tự động tính vận tốc đặt hàng lại của bạn.
              </strong>{' '}
              Thay vì xuất lịch sử giao dịch ra bảng tính và chạy công thức thủ
              công mỗi tháng, Khoai hiển thị trạng thái đặt hàng lại theo thời
              gian thực cho từng SKU — cảnh báo các mặt hàng đang tiếp cận điểm
              đặt hàng trước khi chúng vượt qua. Hệ thống biết thời gian giao
              hàng của nhà cung cấp, theo dõi mức sử dụng trung bình hàng ngày
              và đẩy cảnh báo ngay khi toán học nói &ldquo;đặt hàng ngay.&rdquo;
              Bạn thiết lập tham số một lần. Sự cảnh giác là liên tục.
            </p>
          </div>

          {/* Mục 4 */}
          <h2 class={h2}>
            Khoảng Cách Thời Gian Giao Hàng: Hàng Đang Vận Chuyển Không Phải Là
            Hàng Trên Kệ
          </h2>
          <p class={p}>
            Một trong những nguyên nhân dễ bị bỏ qua nhất tạo ra hàng tồn ảo là{' '}
            <strong class="font-semibold text-gray-900">
              hàng đang vận chuyển
            </strong>{' '}
            — các đơn vị bạn đã đặt từ nhà cung cấp và đã thanh toán, nhưng chưa
            đến nơi. Nhiều hệ thống tồn kho tính số hàng này là "có sẵn." Không
            phải vậy. Đơn hàng của khách hàng không thể được thực hiện từ một
            container vẫn còn cách cảng hai ngày.
          </p>
          <p class={p}>
            Khoảng cách thời gian giao hàng là khoảng thời gian giữa lúc phát
            đơn mua hàng và lúc hàng hóa được kiểm nhập vật lý vào kho và sẵn
            sàng để lấy. Trong khoảng thời gian đó, tồn kho hiển thị của bạn là
            ảo. Bất kỳ hàng nào bạn bán quá dựa trên hàng đang vận chuyển đều là
            một đơn hủy đang chờ xảy ra.
          </p>
          <p class={p}>
            Cách khắc phục là xử lý hàng đang vận chuyển như một ngăn riêng biệt
            trong hệ thống tồn kho — không phải bằng không, không phải có sẵn,
            mà cụ thể là &ldquo;đang về, dự kiến {'{'}ngày{'}'}.&rdquo; Một nền
            tảng phân biệt giữa tồn kho trong tay, đã cam kết và đang về cho bạn
            bức tranh thực tế về những gì bạn thực sự có thể hứa hẹn với khách
            hàng ngay lúc này.
          </p>

          {/* Mục 5 */}
          <h2 class={h2}>Kiểm Kê Vòng: Chấm Dứt Việc Đóng Cửa Kho Hàng Năm</h2>
          <p class={p}>
            Hầu hết các nhà vận hành kho hàng thực hiện một lần kiểm kê mỗi năm.
            Kho dừng hoạt động. Toàn bộ nhân viên đếm hàng. Mất hai ngày, gây ra
            một tuần gián đoạn, và tạo ra một ảnh chụp kỹ thuật chính xác vào
            ngày kiểm kê rồi ngày càng lỗi thời sau đó.
          </p>
          <p class={p}>
            Kiểm kê vòng đảo ngược logic này. Thay vì một lần kiểm kê lớn mỗi
            năm, bạn đếm một khu vực nhỏ mỗi ngày. Kho không bao giờ dừng lại.
            Dữ liệu luôn tươi mới. Cú sốc hàng năm được thay thế bằng sự hiệu
            chỉnh liên tục.
          </p>

          <h3 class={h3}>Cách Xây Dựng Chương Trình Kiểm Kê Vòng</h3>
          <ul class={ul}>
            <li>
              <strong class="font-semibold text-gray-900">
                Hàng hóa nhóm A (top 20% theo doanh thu hoặc tốc độ):
              </strong>{' '}
              Đếm hàng tuần. Đây là những ứng viên tồn kho ảo rủi ro cao nhất vì
              chúng di chuyển nhanh nhất và dễ bán quá nhất.
            </li>
            <li>
              <strong class="font-semibold text-gray-900">
                Hàng hóa nhóm B (tầng giữa):
              </strong>{' '}
              Đếm hàng tháng. Đủ biến động để sai lệch; đủ thường xuyên để phát
              hiện lỗi trước khi chúng lan rộng.
            </li>
            <li>
              <strong class="font-semibold text-gray-900">
                Hàng hóa nhóm C (hàng luân chuyển chậm):
              </strong>{' '}
              Đếm hàng quý. Tốc độ thấp đồng nghĩa rủi ro sai lệch thấp. Hàng
              quý là đủ để phát hiện và điều chỉnh lỗi trước khi chúng gây ra
              bất ngờ hàng tồn kho chết vào cuối năm.
            </li>
          </ul>
          <p class={p}>
            Một kho với 600 SKU chạy chương trình kiểm kê vòng đúng cách sẽ chạm
            vào mọi mặt hàng trong kho khoảng 4–6 lần mỗi năm — so với một lần
            với mô hình truyền thống. Sự khác biệt về mức độ tin cậy dữ liệu
            không phải là tăng dần. Đó là sự thay đổi cơ cấu.
          </p>

          {/* Cycle counting image — 800×500 */}
          <div class="mt-10 overflow-hidden rounded-2xl">
            <Show
              when={!cycleCountFailed()}
              fallback={
                <div class={imgFallback} style={{ 'min-height': '280px' }}>
                  Nhân viên kho bình thản quét một khu vực kệ nhỏ trong kho gọn
                  gàng
                </div>
              }
            >
              <img
                src="/blog-cycle-counting.jpg"
                alt="Nhân viên kho bình thản quét một khu vực kệ trong kho gọn gàng, minh họa sự dễ dàng của kiểm kê vòng hàng ngày"
                width={800}
                height={500}
                class="w-full object-cover"
                loading="lazy"
                onError={() => setCycleCountFailed(true)}
              />
            </Show>
          </div>

          {/* Mục 6 */}
          <h2 class={h2}>
            Cuộc Đua Hạn Sử Dụng: FIFO Và Đồng Hồ Hàng Tồn Kho Chết
          </h2>
          <p class={p}>
            Với bán lẻ, dịch vụ ăn uống, mỹ phẩm và FMCG — bất kỳ danh mục nào
            có hạn sử dụng hoặc ngày hết hạn — hàng tồn ảo có thêm một chiều
            nữa: tuổi. Hàng hóa tồn tại. Được đếm chính xác. Nhưng đã hết hạn,
            hoặc gần hết đến mức không có khách hàng nào chấp nhận.
          </p>
          <p class={p}>
            Biện pháp đối phó tiêu chuẩn là{' '}
            <strong class="font-semibold text-gray-900">
              FIFO (Nhập Trước — Xuất Trước)
            </strong>{' '}
            — thực hành luôn lấy và vận chuyển lô hàng cũ nhất trước. Về lý
            thuyết, FIFO đơn giản. Trên thực tế, nếu không có theo dõi lô ở cấp
            độ hệ thống, nó hoàn toàn phụ thuộc vào kỷ luật của nhân viên kho để
            luân chuyển hàng đúng cách trong mọi lần lấy hàng. Kỷ luật đó sẽ bị
            xói mòn. Hàng mới được xếp trước hàng cũ. Sản phẩm cũ nhất dần di
            chuyển ra phía sau kệ, rồi vào thùng hàng hết hạn, rồi bị xóa sổ.
          </p>
          <p class={p}>
            Theo dõi lô đóng vòng lặp này. Khi mỗi đơn vị được ghi nhận với số
            lô và ngày hết hạn tại thời điểm nhập kho, hệ thống có thể thực thi
            FIFO tự động — sắp xếp danh sách lấy hàng để lô cũ nhất luôn là lô
            đầu tiên được xuất. Nhân viên không cần phải nhớ. Thứ tự lấy hàng
            làm cho lựa chọn đúng trở thành mặc định.
          </p>
          <p class={p}>
            Ngoài quản lý hạn sử dụng, khả năng hiển thị ở cấp lô là thứ biến
            một lệnh thu hồi sản phẩm từ thảm họa vận hành thành công việc mười
            phút. Bạn biết chính xác đơn hàng nào đã nhận lô bị ảnh hưởng. Bạn
            liên hệ trực tiếp với những khách hàng đó. Bạn kiểm soát rủi ro
            trước khi nó lan rộng.
          </p>

          {/* Mục 7 */}
          <h2 class={h2}>Giải Pháp: Đồng Bộ Thời Gian Thực Trên Mọi Kênh</h2>
          <p class={p}>
            Mỗi vấn đề được mô tả trong bài viết này đều có cùng một gốc rễ:
            khoảng cách giữa thực tế vật lý và con số trong hệ thống của bạn.
            Hàng tồn ảo là khoảng cách đó áp dụng cho tình trạng hàng hóa. Hàng
            tồn kho chết là khoảng cách đó áp dụng cho dự báo nhu cầu. Nhầm lẫn
            thời gian giao hàng là khoảng cách đó áp dụng cho logistics nhập
            kho. Thất bại FIFO là khoảng cách đó áp dụng cho trình tự lấy hàng.
          </p>
          <p class={p}>
            Bảng tính không thể thu hẹp những khoảng cách này — không phải vì
            chúng được thiết kế kém, mà vì về cơ bản chúng là thụ động. Bảng
            tính không biết khi có đơn Shopify. Nó không cập nhật khi hàng trả
            về đến bến nhận hàng. Nó không cảnh báo bạn khi một lô đang tiếp cận
            hạn sử dụng. Nó chỉ nằm đó, tĩnh lặng, chờ con người tìm thời gian
            cập nhật. Và trong một kho vận hành 200 lần lấy hàng mỗi ngày, người
            đó không bao giờ không bận.
          </p>
          <p class={p}>
            Đồng bộ thời gian thực có nghĩa là mọi giao dịch — mọi đơn bán, nhập
            kho, trả hàng, điều chỉnh và chuyển kho — đều chảy ngay lập tức vào
            một hồ sơ có thẩm quyền duy nhất. Không có phiên bản sự thật nào bị
            chậm năm phút. Không có sự không chắc chắn kiểu &ldquo;để tôi kiểm
            tra bảng tính.&rdquo; Khi khách hàng hỏi liệu sản phẩm có trong kho
            không, câu trả lời mà nhóm của bạn đưa ra chính là câu trả lời mà
            sàn kho sẽ đưa ra nếu bạn đi ra và nhìn.
          </p>
          <p class={p}>
            Sự nhất quán đó — giữa những gì hệ thống nói và những gì thế giới
            vật lý chứa đựng — là thứ biến quản lý tồn kho từ trách nhiệm pháp
            lý thành tài sản cạnh tranh. Các doanh nghiệp tin tưởng vào con số
            của mình di chuyển nhanh hơn, giữ ít bộ đệm hơn, bán quá ít hơn và
            mất ít khách hàng hơn vì loại email hủy đơn đã mở đầu bài viết này.
          </p>
          <p class="mt-4 text-base font-semibold leading-8 text-gray-800">
            Hàng tồn ảo không phải là vấn đề của kho hàng. Đó là vấn đề dữ liệu.
            Và vấn đề dữ liệu có giải pháp dữ liệu.
          </p>

          {/* CTA */}
          <div class="mt-14 rounded-2xl bg-amber-600 px-8 py-10 text-center text-white">
            <h2 class="text-2xl font-extrabold">
              Biết số tồn kho thực sự của bạn, ngay bây giờ.
            </h2>
            <p class="mt-3 text-base leading-7 text-amber-100">
              Khoai cung cấp cho bạn cái nhìn thống nhất, thời gian thực về hàng
              trong tay, đã cam kết và đang về trên mọi kênh và địa điểm — để
              email hết hàng tiếp theo không bao giờ cần phải viết.
            </p>
            <A
              href="/signup"
              class="mt-6 inline-block rounded-xl bg-white px-8 py-3 text-base font-bold text-amber-700 hover:bg-amber-50"
            >
              Bắt đầu miễn phí với Khoai →
            </A>
          </div>
        </div>
      </article>
    </PublicLayout>
  );
}
